import { FormEvent } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PricingSettings } from '../../services/settings.service';

interface PriceBreakdown {
  materialCostPerUnit: number;
  materialCost: number;
  quantity: number;
  units: number;
  cutCost: number;
  setupCost: number;
  baseCost: number;
  marginRate: number;
  marginAmount: number;
  taxRate: number;
  taxAmount: number;
  vendorCommission: number;
  vendorCommissionAmount: number;
  managerCommission: number;
  managerCommissionAmount: number;
  finalPrice: number;
  pricePerUnit: number;
  variablesSum: number;
}

interface SettingsPageProps {
  // Minimum order state
  minimumOrderValue: number;
  minimumOrderInput: string;
  setMinimumOrderInput: (value: string) => void;
  settingsLoading: boolean;
  settingsSaving: boolean;

  // Pricing settings state
  pricingSettings: PricingSettings;
  pricingInputs: Record<string, string>;
  setPricingInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  pricingLoading: boolean;
  pricingSaving: boolean;

  // Test calculator state
  testCalc: {
    materialCost: string;
    quantity: string;
    units: string;
    width: string;
    height: string;
    thickness: string;
    density: string;
    pricePerKg: string;
  };
  setTestCalc: React.Dispatch<React.SetStateAction<{
    materialCost: string;
    quantity: string;
    units: string;
    width: string;
    height: string;
    thickness: string;
    density: string;
    pricePerKg: string;
  }>>;

  // Price breakdown calculation
  calculatePriceBreakdown: () => PriceBreakdown;

  // Handlers
  handleSaveMinimumOrder: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleSavePricingSettings: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

export function SettingsPage({
  minimumOrderValue,
  minimumOrderInput,
  setMinimumOrderInput,
  settingsLoading,
  settingsSaving,
  pricingSettings,
  pricingInputs,
  setPricingInputs,
  pricingLoading,
  pricingSaving,
  testCalc,
  setTestCalc,
  calculatePriceBreakdown,
  handleSaveMinimumOrder,
  handleSavePricingSettings
}: SettingsPageProps) {
  const testPriceBreakdown = calculatePriceBreakdown();

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Orçamento</span>
        <span>/</span>
        <span className="font-medium text-foreground">Configurações</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Configurações de Orçamento</CardTitle>
          <CardDescription>
            Gerencie as configurações gerais do sistema de orçamento.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSaveMinimumOrder} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="minimum-order">Valor Mínimo do Pedido (R$)</Label>
              <div className="flex gap-3 items-start">
                <div className="flex-1 space-y-1">
                  <Input
                    id="minimum-order"
                    type="number"
                    step="0.01"
                    min="0"
                    value={minimumOrderInput}
                    onChange={(event) => setMinimumOrderInput(event.target.value)}
                    placeholder="Ex: 100.00"
                    disabled={settingsLoading || settingsSaving}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Define o valor mínimo que um cliente precisa pedir. Valor atual: R$ {minimumOrderValue.toFixed(2)}
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={settingsLoading || settingsSaving}
                  className="mt-0"
                >
                  {settingsSaving ? 'Salvando…' : 'Salvar'}
                </Button>
              </div>
            </div>
          </form>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Configurações de Precificação</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Configure os custos fixos e variáveis da fórmula de precificação.
              <br />
              <span className="font-medium">Fórmula:</span> Preço = (Material + Corte + Setup + Margem) + Impostos + Comissões
            </p>

            <form onSubmit={handleSavePricingSettings} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column: Settings */}
                <div className="space-y-4">
                  {/* Cut Cost Per Unit */}
                  <div className="space-y-2">
                    <Label htmlFor="cut-cost">Custo de Corte por Unidade (R$)</Label>
                    <Input
                      id="cut-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingInputs.cutCostPerUnit ?? pricingSettings.cutCostPerUnit}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, cutCostPerUnit: e.target.value }))}
                      placeholder="Ex: 0.45"
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Custo por peça cortada. Valor atual: R$ {pricingSettings.cutCostPerUnit.toFixed(2)}
                    </p>
                  </div>

                  {/* Setup Cost Per Order */}
                  <div className="space-y-2">
                    <Label htmlFor="setup-cost">Custo de Setup por Pedido (R$)</Label>
                    <Input
                      id="setup-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricingInputs.setupCostPerOrder ?? pricingSettings.setupCostPerOrder}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, setupCostPerOrder: e.target.value }))}
                      placeholder="Ex: 2.00"
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Custo fixo por pedido. Valor atual: R$ {pricingSettings.setupCostPerOrder.toFixed(2)}
                    </p>
                  </div>

                  {/* Margin Rate */}
                  <div className="space-y-2">
                    <Label htmlFor="margin-rate">Taxa de Margem</Label>
                    <Input
                      id="margin-rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={pricingInputs.marginRate ?? pricingSettings.marginRate}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, marginRate: e.target.value }))}
                      placeholder="Ex: 0.35"
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Margem de lucro (0-1). Valor atual: {(pricingSettings.marginRate * 100).toFixed(0)}%
                    </p>
                  </div>

                  {/* Margin Tiers */}
                  <div className="space-y-2">
                    <Label htmlFor="margin-tiers">Faixas de Margem (Opcional)</Label>
                    <Input
                      id="margin-tiers"
                      type="text"
                      value={pricingInputs.marginTiers ?? pricingSettings.marginTiers ?? ''}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, marginTiers: e.target.value }))}
                      placeholder='Ex: 1-5:0.35, 6-10:0.32, >10:0.30'
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Formato: quantidade-quantidade:margem (ex: 1-5:0.35, 6-10:0.32, &gt;10:0.30)
                      {pricingSettings.marginTiers && (
                        <><br />Atual: {pricingSettings.marginTiers}</>
                      )}
                    </p>
                  </div>

                  {/* Tax Rate */}
                  <div className="space-y-2">
                    <Label htmlFor="tax-rate">Taxa de Impostos</Label>
                    <Input
                      id="tax-rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={pricingInputs.taxRate ?? pricingSettings.taxRate}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, taxRate: e.target.value }))}
                      placeholder="Ex: 0.10"
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Percentual de impostos (0-1). Valor atual: {(pricingSettings.taxRate * 100).toFixed(0)}%
                    </p>
                  </div>

                  {/* Vendor Commission */}
                  <div className="space-y-2">
                    <Label htmlFor="vendor-commission">Comissão do Vendedor</Label>
                    <Input
                      id="vendor-commission"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={pricingInputs.vendorCommission ?? pricingSettings.vendorCommission}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, vendorCommission: e.target.value }))}
                      placeholder="Ex: 0.05"
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Comissão do vendedor (0-1). Valor atual: {(pricingSettings.vendorCommission * 100).toFixed(0)}%
                    </p>
                  </div>

                  {/* Manager Commission */}
                  <div className="space-y-2">
                    <Label htmlFor="manager-commission">Comissão do Gerente</Label>
                    <Input
                      id="manager-commission"
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={pricingInputs.managerCommission ?? pricingSettings.managerCommission}
                      onChange={(e) => setPricingInputs(prev => ({ ...prev, managerCommission: e.target.value }))}
                      placeholder="Ex: 0.01"
                      disabled={pricingLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Comissão do gerente (0-1). Valor atual: {(pricingSettings.managerCommission * 100).toFixed(0)}%
                    </p>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4 mt-6">
                    <p className="text-sm font-medium mb-2">Resumo da Fórmula</p>
                    <p className="text-xs text-muted-foreground">
                      Custos Fixos = (R$ {pricingSettings.cutCostPerUnit.toFixed(2)} × unidades) + R$ {pricingSettings.setupCostPerOrder.toFixed(2)}
                      <br />
                      Variáveis Totais = {((pricingSettings.marginRate + pricingSettings.taxRate + pricingSettings.vendorCommission + pricingSettings.managerCommission) * 100).toFixed(0)}%
                      (Margem {(pricingSettings.marginRate * 100).toFixed(0)}% + Impostos {(pricingSettings.taxRate * 100).toFixed(0)}% + Comissões {((pricingSettings.vendorCommission + pricingSettings.managerCommission) * 100).toFixed(0)}%)
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={pricingLoading || pricingSaving}
                    className="w-full"
                  >
                    {pricingSaving ? 'Salvando…' : 'Salvar Todas as Configurações'}
                  </Button>
                </div>

                {/* Right Column: Live Preview */}
                <div className="space-y-4">
                  <div className="sticky top-20">
                    <div className="bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
                      <h4 className="font-semibold mb-3">Calculadora de Teste</h4>
                      <p className="text-xs text-muted-foreground mb-4">
                        Insira valores de teste para ver como cada configuração afeta o preço final.
                      </p>

                      <div className="space-y-3 mb-4">
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-3 space-y-2">
                          <p className="text-xs font-medium">Cálculo por Dimensões (Opcional)</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label htmlFor="test-width" className="text-xs">Largura (cm)</Label>
                              <Input
                                id="test-width"
                                type="number"
                                step="0.1"
                                min="0"
                                value={testCalc.width}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, width: e.target.value }))}
                                placeholder="Ex: 100"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label htmlFor="test-height" className="text-xs">Altura (cm)</Label>
                              <Input
                                id="test-height"
                                type="number"
                                step="0.1"
                                min="0"
                                value={testCalc.height}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, height: e.target.value }))}
                                placeholder="Ex: 150"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label htmlFor="test-thickness" className="text-xs">Espessura (mm)</Label>
                              <Input
                                id="test-thickness"
                                type="number"
                                step="0.1"
                                min="0"
                                value={testCalc.thickness}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, thickness: e.target.value }))}
                                placeholder="Ex: 3"
                                className="mt-1 h-8"
                              />
                            </div>
                            <div>
                              <Label htmlFor="test-density" className="text-xs">Densidade (g/cm³)</Label>
                              <Input
                                id="test-density"
                                type="number"
                                step="0.01"
                                min="0"
                                value={testCalc.density}
                                onChange={(e) => setTestCalc(prev => ({ ...prev, density: e.target.value }))}
                                placeholder="Ex: 1.19"
                                className="mt-1 h-8"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="test-price-per-kg" className="text-xs">Preço por Kg (R$/kg)</Label>
                            <Input
                              id="test-price-per-kg"
                              type="number"
                              step="0.01"
                              min="0"
                              value={testCalc.pricePerKg}
                              onChange={(e) => setTestCalc(prev => ({ ...prev, pricePerKg: e.target.value }))}
                              placeholder="Ex: 12.50"
                              className="mt-1 h-8"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="test-material-cost" className="text-xs">OU Custo do Material Direto (R$)</Label>
                          <Input
                            id="test-material-cost"
                            type="number"
                            step="0.01"
                            min="0"
                            value={testCalc.materialCost}
                            onChange={(e) => setTestCalc(prev => ({ ...prev, materialCost: e.target.value }))}
                            placeholder="Ex: 50.00"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="test-units" className="text-xs">Quantidade de Unidades</Label>
                          <Input
                            id="test-units"
                            type="number"
                            step="1"
                            min="1"
                            value={testCalc.units}
                            onChange={(e) => setTestCalc(prev => ({ ...prev, units: e.target.value }))}
                            placeholder="Ex: 1"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div className="border-t border-blue-200 dark:border-blue-800 pt-4 space-y-2">
                        <h5 className="font-medium text-sm mb-3">Breakdown de Custos</h5>

                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Custo Material ({testPriceBreakdown.units}× R$ {testPriceBreakdown.materialCostPerUnit.toFixed(2)}):</span>
                            <span className="font-medium">R$ {testPriceBreakdown.materialCost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Custo Corte ({testPriceBreakdown.units}× R$ {testPriceBreakdown.units > 0 ? (testPriceBreakdown.cutCost / testPriceBreakdown.units).toFixed(2) : '0.00'}):</span>
                            <span className="font-medium">+ R$ {testPriceBreakdown.cutCost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Custo Setup:</span>
                            <span className="font-medium">+ R$ {testPriceBreakdown.setupCost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                            <span className="text-muted-foreground font-medium">Custos Base:</span>
                            <span className="font-semibold">R$ {testPriceBreakdown.baseCost.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="border-t border-blue-200 dark:border-blue-800 pt-3 mt-3 space-y-1 text-xs">
                          <h6 className="font-medium text-xs mb-2">Variáveis Aplicadas:</h6>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Margem ({(testPriceBreakdown.marginRate * 100).toFixed(0)}%):</span>
                            <span className="font-medium text-green-600 dark:text-green-400">+ R$ {testPriceBreakdown.marginAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Impostos ({(testPriceBreakdown.taxRate * 100).toFixed(0)}%):</span>
                            <span className="font-medium text-orange-600 dark:text-orange-400">+ R$ {testPriceBreakdown.taxAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Comissão Vendedor ({(testPriceBreakdown.vendorCommission * 100).toFixed(0)}%):</span>
                            <span className="font-medium text-purple-600 dark:text-purple-400">+ R$ {testPriceBreakdown.vendorCommissionAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Comissão Gerente ({(testPriceBreakdown.managerCommission * 100).toFixed(0)}%):</span>
                            <span className="font-medium text-purple-600 dark:text-purple-400">+ R$ {testPriceBreakdown.managerCommissionAmount.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="border-t-2 border-blue-300 dark:border-blue-700 pt-3 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">Preço Final:</span>
                            <span className="text-lg font-bold text-blue-600 dark:text-blue-400">R$ {testPriceBreakdown.finalPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center mt-1 text-xs">
                            <span className="text-muted-foreground">Preço por Unidade:</span>
                            <span className="font-medium">R$ {testPriceBreakdown.pricePerUnit.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="bg-blue-100 dark:bg-blue-900/50 rounded p-2 mt-3 text-xs">
                          <p className="text-muted-foreground">
                            Variáveis Totais: <span className="font-medium">{(testPriceBreakdown.variablesSum * 100).toFixed(1)}%</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {settingsLoading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
